import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Printer, Send, Info, MessageSquare } from "lucide-react";
import { PageHero, DraftNotice } from "@/components/common/SectionHeading";
import { fetchApplicantTypes, fetchRequirements } from "@/lib/lnlApi";

export default function ApplicantTypePage() {
    const { code } = useParams();
    const navigate = useNavigate();
    const [type, setType] = useState(null);
    const [reqs, setReqs] = useState(null);
    useEffect(() => {
        fetchApplicantTypes().then((all) => setType(all.find((t) => t.code === code) || null));
        fetchRequirements(code).then(setReqs).catch(() => setReqs(null));
    }, [code]);
    if (!type) return <div className="lnl-container py-16"><div className="text-lnl-gray">Loading applicant path…</div></div>;

    const itemNotes = reqs?.item_notes || {};

    return (
        <div data-testid={`applicant-type-${code}`}>
            <PageHero
                eyebrow={`Applicant path · ${type.level.toUpperCase()}`}
                title={type.label}
                description={type.long}
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Admissions", to: "/admissions" }, { label: type.label }]}
                cta={
                    <div className="mt-6 flex flex-wrap gap-3">
                        <button onClick={() => navigate(`/admissions/apply?type=${code}`)} className="lnl-btn-primary" data-testid={`start-app-${code}`}>Start Application <ArrowRight className="h-4 w-4" /></button>
                        <Link to="/contact" className="lnl-btn-secondary" data-testid={`ask-${code}`}>Ask Admissions</Link>
                        <button onClick={() => window.print()} className="lnl-link inline-flex items-center gap-1 py-2.5"><Printer className="h-4 w-4" /> Print requirements</button>
                    </div>
                }
            />
            <section className="py-12 bg-white">
                <div className="lnl-container grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                        <h2 className="font-heading text-2xl font-bold text-lnl-charcoal" data-testid="requirements-title">
                            {reqs?.title || "Admission requirements"}
                        </h2>
                        <div className="mt-3"><DraftNotice tone="warn">Admission requirements may be updated. Contact the LNL Admissions Office to confirm the latest documents before submission.</DraftNotice></div>
                        <ol className="mt-5 space-y-3 list-none" data-testid="requirements-list">
                            {(reqs?.items || []).map((r, i) => {
                                const note = itemNotes[String(i)] || itemNotes[i];
                                return (
                                    <li key={i} className="flex items-start gap-3 lnl-card p-4" data-testid={`req-item-${i}`}>
                                        <div className="w-7 h-7 rounded-full bg-lnl-green-bg text-lnl-green-deep grid place-items-center shrink-0 font-heading font-bold text-xs">
                                            {i + 1}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-lnl-charcoal font-heading font-semibold leading-snug">{r}</div>
                                            {note && (
                                                <div
                                                    className="mt-2 rounded-md bg-lnl-gold-soft/20 border border-lnl-gold-soft/50 p-2.5 flex items-start gap-2"
                                                    data-testid={`req-item-note-${i}`}
                                                >
                                                    <Info className="h-4 w-4 text-lnl-gold shrink-0 mt-0.5" />
                                                    <div className="text-xs text-lnl-charcoal leading-relaxed">
                                                        {note}
                                                        <div className="mt-2">
                                                            <Link
                                                                to="/contact"
                                                                className="inline-flex items-center gap-1 text-[11px] font-heading font-bold text-lnl-green-deep hover:text-lnl-green"
                                                                data-testid={`req-item-note-cta-${i}`}
                                                            >
                                                                <MessageSquare className="h-3.5 w-3.5" /> Message LNL Admissions
                                                            </Link>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <CheckCircle2 className="h-5 w-5 text-lnl-green mt-0.5 shrink-0" />
                                    </li>
                                );
                            })}
                        </ol>
                        {reqs?.notice && <div className="mt-4"><DraftNotice tone="warn">{reqs.notice}</DraftNotice></div>}
                    </div>
                    <aside className="lnl-card p-5 h-fit">
                        <div className="lnl-eyebrow">Who this path is for</div>
                        <div className="font-heading font-bold text-lnl-charcoal mt-1">{type.audience}</div>
                        <div className="mt-4 space-y-2 text-sm">
                            <Link to={`/admissions/apply?type=${code}`} className="lnl-btn-primary w-full justify-center"><Send className="h-4 w-4" /> Start application</Link>
                            <Link to="/tuition-scholarships" className="lnl-btn-secondary w-full justify-center">Ask about tuition</Link>
                        </div>
                    </aside>
                </div>
            </section>
        </div>
    );
}
